import React from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';

import useAuth from '@HOOKS/useAuth';
import useGlobalPermissions from '@HOOKS/useGlobalPermissions';
import useSuperAdminPermissions from '@HOOKS/useSuperAdminPermissions';

import Subheader from '@COMPONENTS/HEADERS/Subheader';
import IconSquareButton from '@COMPONENTS/COMMON/buttons/IconSquareButton';
import Grid from '@COMPONENTS/SHARED/Grid';

import styles from './Main.module.scss';

const cx: CX = classnames.bind(styles);

// Wave hand emoji component
function WaveHandIcon() {
    return (
        <span style={{ fontSize: '32px' }}>👋</span>
    );
}

function MainPage() {
    const { state: { user } } = useAuth();
    const {
        canCreateDocuments,
        canManageVendors,
        canViewLogs,
        canManagePermissions,
    } = useGlobalPermissions();

    const isSuperAdmin = useSuperAdminPermissions();

    const navigate = useNavigate();

    // Get user's first name
    const getFirstName = () => {
        if (!user?.fullName) return 'there';
        return user.fullName.split(' ')[0];
    };

    // Get time-based greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    return (
        <div className={cx('page')}>
            <Subheader title="Dashboard" />

            <div className={cx('content-wrapper')}>
                <div className="container max-constraint center">
                    {/* Welcome Section */}
                    <div className={cx('welcome-section')}>
                        <div className={cx('welcome-content')}>
                            <div className={cx('greeting-container')}>
                                <div className={cx('handshake-icon')}>
                                    <WaveHandIcon />
                                </div>
                                <div className={cx('greeting-text')}>
                                    <h1 className={cx('greeting-title')}>
                                        {getGreeting()}
                                        ,
                                        {' '}
                                        {getFirstName()}
                                        !
                                    </h1>
                                    <p className={cx('greeting-subtitle')}>
                                        Welcome to your document management dashboard. What would you like to do today?
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats Section */}
                    <div className={cx('stats-section')}>
                        <div className={cx('stats-grid')}>
                            <div className={cx('stat-card')}>
                                <div className={cx('stat-icon', 'documents')}>
                                    📄
                                </div>
                                <div className={cx('stat-content')}>
                                    <h3 className={cx('stat-title')}>Document Templates</h3>
                                    <p className={cx('stat-description')}>Manage and organize your templates</p>
                                </div>
                            </div>
                            <div className={cx('stat-card')}>
                                <div className={cx('stat-icon', 'generation')}>
                                    ⚡
                                </div>
                                <div className={cx('stat-content')}>
                                    <h3 className={cx('stat-title')}>Quick Generation</h3>
                                    <p className={cx('stat-description')}>Generate documents in minutes</p>
                                </div>
                            </div>
                            <div className={cx('stat-card')}>
                                <div className={cx('stat-icon', 'settings')}>
                                    ⚙️
                                </div>
                                <div className={cx('stat-content')}>
                                    <h3 className={cx('stat-title')}>Easy Configuration</h3>
                                    <p className={cx('stat-description')}>Customize settings to your needs</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Actions Section */}
                    <div className={cx('actions-section')}>
                        <h2 className={cx('section-title')}>Main Actions</h2>
                        <Grid>
                            <IconSquareButton
                                // locked={!canGenerateDocuments}
                                locked={false}
                                title="Generate New Document"
                                icon="document-generation"
                                onClick={() => {
                                    navigate(ROUTES.chooseDocument.path);
                                }}
                            />

                            <IconSquareButton
                                locked={!canCreateDocuments}
                                title="Manage Document Templates"
                                icon="document-admin"
                                onClick={() => {
                                    navigate(ROUTES.admin.documents.path);
                                }}
                            />

                            <IconSquareButton
                                locked={!canManageVendors}
                                title="Manage Vendors"
                                icon="vendors"
                                onClick={() => {
                                    navigate(ROUTES.admin.manageVendors.path);
                                }}
                            />

                            <IconSquareButton
                                locked={!canManagePermissions}
                                title="Manage Users"
                                icon="user-permissions"
                                onClick={() => {
                                    navigate(ROUTES.admin.manageUsersPermissions.path);
                                }}
                            />

                            <IconSquareButton
                                locked={!canViewLogs}
                                title="Logs"
                                icon="logs"
                                onClick={() => {
                                    navigate(ROUTES.admin.logs.path);
                                }}
                            />

                            <IconSquareButton
                                locked={!canManagePermissions}
                                title="App & Document Settings"
                                icon="app-settings"
                                onClick={() => {
                                    navigate(ROUTES.admin.appSettings.path);
                                }}
                            />

                            {isSuperAdmin && (
                                <IconSquareButton
                                    locked={false}
                                    title="System Settings"
                                    icon="system-settings"
                                    onClick={() => {
                                        navigate(ROUTES.admin.systemSettings.path);
                                    }}
                                />
                            )}
                        </Grid>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MainPage;
